import React, { useState, useRef, useEffect } from "react";
import { collection, Timestamp, query, orderBy, getDocs, limit, startAfter, doc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase-config";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../backend/hooks/local-storage/use-local-storage";
import { useUser } from "../../backend/hooks/user/use-user";
import useAuthCallback from "../../backend/hooks/auth/use-auth-callback";
import { AppRoutes, Modal, Loader } from "../../reusable";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAlerts } from "../../backend/hooks/alert/use-alert";
import "./job-board-styles.css";
import ResumeUploadModal from "../../components/ResumeUploadModal";
import { ReactComponent as SpotIllustration } from '../../reusable/assets/svgs/Spot Ilustrations.svg';

const FILTERS = {
  locationType: ["Onsite", "Remote", "Hybrid"],
  employmentType: ["Full-time", "Part-time", "Contract"], // Match backend values exactly
  company: [], // For UI, can be left empty or add sample
};

function formatPostedTime(createdAt) {
  if (!createdAt) return '';
  let date;
  if (createdAt instanceof Timestamp) {
    date = createdAt.toDate();
  } else if (createdAt.seconds) {
    date = new Date(createdAt.seconds * 1000);
  } else {
    date = new Date(createdAt);
  }
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

function FadeSection({ show, children, className = "" }) {
  const [visible, setVisible] = useState(show);
  const [fade, setFade] = useState(show ? "fade-in" : "");
  const timeoutRef = useRef();

  React.useEffect(() => {
    if (show) {
      setVisible(true);
      setFade("fade-in");
    } else if (visible) {
      setFade("fade-out");
      timeoutRef.current = setTimeout(() => {
        setVisible(false);
      }, 220);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [show, visible]);

  if (!visible) return null;
  return <div className={`${fade} ${className}`}>{children}</div>;
}

function FilterDropdown({ label, options, selected, setSelected, open, setOpen }) {
  const [temp, setTemp] = useState(selected);
  const [fadeOut, setFadeOut] = useState(false);

  // Handle smooth fade-out
  const closeDropdown = () => {
    setFadeOut(true);
    setTimeout(() => {
      setFadeOut(false);
      setOpen(null);
    }, 180);
  };

  return (
    <div className="kydoscope-filter-dropdown">
      <button
        className="kydoscope-filter-btn"
        onClick={e => {
          e.preventDefault();
          if (open === label) {
            closeDropdown();
          } else {
            setOpen(label);
          }
        }}
      >
        {label} <span className="kydoscope-filter-caret">&#9662;</span>
      </button>
      {open === label && (
        <div className={`kydoscope-filter-dropdown-content${fadeOut ? ' fade-out' : ''}`}>
          {options.length > 0 ? (
            options.map(opt => (
              <label key={opt} className="kydoscope-filter-option">
                <input
                  type="checkbox"
                  checked={temp.includes(opt)}
                  onChange={() => {
                    setTemp(temp.includes(opt) ? temp.filter(o => o !== opt) : [...temp, opt]);
                  }}
                />
                {opt}
              </label>
            ))
          ) : (
            <input
              className="kydoscope-filter-company-input"
              placeholder="Enter company name"
              value={temp[0] || ""}
              onChange={e => setTemp([e.target.value])}
            />
          )}
          <div className="kydoscope-filter-actions">
            <button
              className="kydoscope-filter-reset"
              onClick={e => {
                e.preventDefault();
                setTemp([]);
              }}
            >Reset</button>
            <button
              className="kydoscope-filter-apply"
              onClick={e => {
                e.preventDefault();
                setSelected(temp);
                closeDropdown();
              }}
            >Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function JobBoard() {
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [searching, setSearching] = useState(false);
  const [filterOpen, setFilterOpen] = useState(null);
  const [locationType, setLocationType] = useState([]);
  const [employmentType, setEmploymentType] = useState([]);
  const [company, setCompany] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsError, setJobsError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  
  // Job caching with 5-minute duration
  const jobsCacheRef = useRef(new Map());
  const CACHE_DURATION = 300000; // 5 minutes
  const navigate = useNavigate();
  const { isMobileScreen } = useLocalStorage();
  const { loggedInUser } = useUser();
  const { addAlert } = useAlerts();
  const protectedLoginRedirect = useAuthCallback(() => {
    if (selectedJob) navigate(`/jobs/${selectedJob.id}`);
  });

  useEffect(() => {
    const fetchTotal = async () => {
      const q = query(collection(db, "job-postings"));
      const snap = await getDocs(q);
      setTotalCount(snap.size);
    };
    fetchTotal();
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchInitial = async () => {
      try {
        const cacheKey = 'initial_jobs';
        const cached = jobsCacheRef.current.get(cacheKey);
        const now = Date.now();
        
        // Check if we have cached data that's still valid
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
          console.log('JobBoard: Using cached job data, jobs:', cached.jobs.length);
          setJobs(cached.jobs);
          setSelectedJob(cached.jobs[0] || null);
          setLastDoc(cached.lastDoc);
          setLoadedCount(cached.jobs.length);
          setHasMore(cached.hasMore);
          setLoading(false);
          return;
        }
        
        console.log('JobBoard: Fetching fresh job data');
        
        const q = query(collection(db, "job-postings"), orderBy("createdAt", "desc"), limit(10));
        const snap = await getDocs(q);
        const jobsData = snap.docs.map(doc => ({
          id: doc.id,
          title: doc.data().jobPosition || '',
          company: doc.data().companyName || '',
          location: (doc.data().jobLocation && doc.data().jobLocation.place) || '',
          locationType: doc.data().jobType || '',
          logo: doc.data().profilePicture || '/favicon.svg',
          posted: formatPostedTime(doc.data().createdAt),
          description: doc.data().jobDescription || '',
          jobPosition: doc.data().jobPosition || '',
          jobSalary: doc.data().jobSalary || '',
          jobType: doc.data().jobType || '',
          requiredEducation: doc.data().requiredEducation || '',
          requiredExperience: doc.data().requiredExperience || '',
          requiredSoftwareSkills: doc.data().requiredSoftwareSkills || '',
          ...doc.data()
        }));
        
        const lastDocument = snap.docs[snap.docs.length - 1];
        const hasMoreData = snap.docs.length === 10;
        
        // Cache the results
        jobsCacheRef.current.set(cacheKey, {
          jobs: jobsData,
          lastDoc: lastDocument,
          hasMore: hasMoreData,
          timestamp: now
        });
        
        console.log('JobBoard: Cached fresh job data, jobs:', jobsData.length);
        
        setJobs(jobsData);
        setSelectedJob(jobsData[0] || null);
        setLastDoc(lastDocument);
        setLoadedCount(jobsData.length);
        setHasMore(snap.docs.length === 10);
        setJobsError(null);
      } catch (err) {
        setJobsError("Failed to load jobs");
        setJobs([]);
        setSelectedJob(null);
      }
      setLoading(false);
    };
    fetchInitial();
  }, []);

  const handleNext = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;
    setLoadingMore(true);
    try {
      const q = query(collection(db, "job-postings"), orderBy("createdAt", "desc"), startAfter(lastDoc), limit(10));
      const snap = await getDocs(q);
      if (snap.empty || snap.docs.length === 0) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }
      const jobsData = snap.docs.map(doc => ({
        id: doc.id,
        title: doc.data().jobPosition || '',
        company: doc.data().companyName || '',
        location: (doc.data().jobLocation && doc.data().jobLocation.place) || '',
        locationType: doc.data().jobType || '',
        logo: doc.data().profilePicture || '/favicon.svg',
        posted: formatPostedTime(doc.data().createdAt),
        description: doc.data().jobDescription || '',
        jobPosition: doc.data().jobPosition || '',
        jobSalary: doc.data().jobSalary || '',
        jobType: doc.data().jobType || '',
        requiredEducation: doc.data().requiredEducation || '',
        requiredExperience: doc.data().requiredExperience || '',
        requiredSoftwareSkills: doc.data().requiredSoftwareSkills || '',
        ...doc.data()
      }));
      setJobs(prev => [...prev, ...jobsData]);
      setLastDoc(snap.docs[snap.docs.length - 1]);
      setLoadedCount(prev => prev + jobsData.length);
      setHasMore(snap.docs.length === 10);
    } catch (err) {
      setJobsError("Failed to load more jobs");
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    if (!filterOpen) return;
    function handleClick(e) {
      const dropdowns = document.querySelectorAll('.kydoscope-filter-dropdown-content');
      let clickedInside = false;
      dropdowns.forEach(dropdown => {
        if (dropdown.contains(e.target)) clickedInside = true;
      });
      const filterBtns = document.querySelectorAll('.kydoscope-filter-btn');
      filterBtns.forEach(btn => {
        if (btn.contains(e.target)) clickedInside = true;
      });
      if (!clickedInside) setFilterOpen(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filterOpen]);

  // Updated filtering logic to include all filters (seniority removed)
  const filteredJobs = jobs.filter((job) => {
    // Keyword filter (title or company)
    const keywordMatch =
      keyword.trim() === "" ||
      job.title?.toLowerCase().includes(keyword.toLowerCase()) ||
      job.company?.toLowerCase().includes(keyword.toLowerCase());
    // Location filter
    const locationMatch =
      location.trim() === "" ||
      (job.location && job.location.toLowerCase().includes(location.toLowerCase()));
    // Location Type filter (jobType is used for locationType in data mapping)
    const locationTypeMatch =
      locationType.length === 0 || locationType.includes(job.locationType);
    // Employment Type filter (should match job.jobType)
    const employmentTypeMatch =
      employmentType.length === 0 || employmentType.includes(job.jobType);
    // Company filter (by name)
    const companyMatch =
      company.length === 0 || company.some((c) => job.company?.toLowerCase().includes(c.toLowerCase()));
    return (
      keywordMatch &&
      locationMatch &&
      locationTypeMatch &&
      employmentTypeMatch &&
      companyMatch
    );
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    setSearching(true);
    setTimeout(() => {
      setLoading(false);
    }, 900);
  };

  const handleApply = async () => {
    if (!loggedInUser?.email) {
      protectedLoginRedirect();
      return;
    }
    const userProfileRef = doc(db, "creator-profiles", loggedInUser.userId);
    const userProfileSnap = await getDoc(userProfileRef);
    if (!userProfileSnap.exists()) {
      navigate(AppRoutes.FILL_PROFILE_DETAILS.replace(":userId", loggedInUser.userId));
      return;
    }
    const userProfile = userProfileSnap.data();
    if (!userProfile.lookingForJob) {
      addAlert({
        type: "error",
        message: "Please edit your profile and set job seeking toggle on to apply for jobs",
        autoHide: true
      });
      return;
    }
    if (!userProfile.resume) {
      setShowResumeModal(true);
      return;
    }
    setIsApplying(true);
    try {
      const functions = getFunctions();
      const sendJobApplication = httpsCallable(functions, 'sendJobApplication');
      await sendJobApplication({
        jobId: selectedJob.id,
        userId: loggedInUser.userId
      });
      addAlert({
        type: "success",
        message: "Application submitted successfully!",
        autoHide: true
      });
    } catch (error) {
      console.error("Error applying for job:", error);
      let errorMsg = "Failed to submit application. Please try again.";
      if (error?.message?.includes("You have already applied to this job.")) {
        errorMsg = "You have already applied to this job.";
      } else if (error?.code === 'functions/already-exists' && error?.details) {
        errorMsg = error.details;
      }
      addAlert({
        type: "error",
        message: errorMsg,
        autoHide: true
      });
    }
    setIsApplying(false);
  };

  function JobListSkeleton() {
    return (
      <div className="kydoscope-job-list" style={{opacity:0.7, minWidth:320, width:320, maxWidth:320, minHeight:'100%'}}>
        {[...Array(4)].map((_,i) => (
          <div key={i} className="kydoscope-job-summary" style={{pointerEvents:'none'}}>
            <div style={{width:48,height:48,borderRadius:10,background:'#e5e7eb',marginRight:18}}></div>
            <div style={{flex:1}}>
              <div style={{height:18,width:'60%',background:'#e5e7eb',borderRadius:6,marginBottom:8}}></div>
              <div style={{height:14,width:'40%',background:'#e5e7eb',borderRadius:6,marginBottom:6}}></div>
              <div style={{height:14,width:'50%',background:'#e5e7eb',borderRadius:6,marginBottom:6}}></div>
              <div style={{height:12,width:'30%',background:'#e5e7eb',borderRadius:6}}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function JobDetailSkeleton() {
    return (
      <div className="kydoscope-job-detail" style={{opacity:0.7}}>
        <div className="kydoscope-job-detail-header">
          <div style={{width:64,height:64,borderRadius:12,background:'#e5e7eb',marginRight:22}}></div>
          <div style={{flex:1}}>
            <div style={{height:22,width:'60%',background:'#e5e7eb',borderRadius:8,marginBottom:10}}></div>
            <div style={{height:16,width:'40%',background:'#e5e7eb',borderRadius:8,marginBottom:8}}></div>
            <div style={{height:16,width:'50%',background:'#e5e7eb',borderRadius:8}}></div>
          </div>
        </div>
        <div style={{height:36,width:'30%',background:'#e5e7eb',borderRadius:8,margin:'18px 0 28px 0'}}></div>
        <div style={{height:18,width:'40%',background:'#e5e7eb',borderRadius:8,marginBottom:12}}></div>
        <div style={{height:60,width:'100%',background:'#e5e7eb',borderRadius:8,marginBottom:18}}></div>
        <div style={{height:18,width:'40%',background:'#e5e7eb',borderRadius:8,marginBottom:12}}></div>
        <div style={{height:60,width:'100%',background:'#e5e7eb',borderRadius:8}}></div>
      </div>
    );
  }

  return (
    <div className="kydoscope-dashboard-bg">
      <main className={`kydoscope-dashboard-main kydoscope-dashboard-center${searching ? ' searching' : ''}`}>
        <div className="kydoscope-dashboard-titleblock">
          <span className="kydoscope-title">Job Search</span>
        </div>
        <form className="kydoscope-search-form" onSubmit={handleSearch} style={{position:'relative',width:'100%',maxWidth:700}}>
          <input
            className="kydoscope-search-input"
            type="text"
            placeholder="Keyword"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
          />
          <input
            className="kydoscope-search-input"
            type="text"
            placeholder="Location"
            value={location}
            onChange={e => setLocation(e.target.value)}
          />
          <button className="kydoscope-search-btn" type="submit">Search</button>
        </form>
        <FadeSection show={searching} className="kydoscope-filters-row">
          {!isMobileScreen && (
            <FilterDropdown
              label="Location Type"
              options={FILTERS.locationType}
              selected={locationType}
              setSelected={setLocationType}
              open={filterOpen}
              setOpen={setFilterOpen}
            />
          )}
          <FilterDropdown
            label="Employment Type"
            options={FILTERS.employmentType}
            selected={employmentType}
            setSelected={setEmploymentType}
            open={filterOpen}
            setOpen={setFilterOpen}
          />
          <FilterDropdown
            label="Company"
            options={FILTERS.company}
            selected={company}
            setSelected={setCompany}
            open={filterOpen}
            setOpen={setFilterOpen}
          />
          <button className="kydoscope-filter-reset-main" onClick={e => {
            e.preventDefault();
            setKeyword("");
            setLocation("");
            setLocationType([]);
            setEmploymentType([]);
            setCompany([]);
          }}>Reset</button>
        </FadeSection>
        <FadeSection show={searching} className="kydoscope-job-results">
          {jobsError ? (
            <div className="kydoscope-no-jobs">{jobsError}</div>
          ) : loading ? (
            <>
              <JobListSkeleton />
              <JobDetailSkeleton />
            </>
          ) : (
            <>
              <div className="kydoscope-job-list">
                {filteredJobs.length > 0 ? (
                  filteredJobs.map((job) => (
                    <div
                      key={job.id}
                      className={`kydoscope-job-summary${selectedJob && selectedJob.id === job.id ? " selected" : ""}`}
                      onClick={() => isMobileScreen ? navigate(`/jobs/${job.id}`) : setSelectedJob(job)}
                      tabIndex={0}
                      style={{ alignItems: 'flex-start' }} // Ensure top alignment
                    >
                      <img
                        src={job.logo}
                        alt={job.company || 'Company logo'}
                        className="kydoscope-job-logo"
                        style={{ marginTop: 0, marginBottom: 0, marginRight: 12, alignSelf: 'flex-start' }}
                        loading="lazy"
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, paddingLeft: 0, justifyContent: 'flex-start' }}>
                        <div className="kydoscope-job-title" style={{ marginBottom: 2 }}>{job.title}</div>
                        <div className="kydoscope-job-company" style={{ marginBottom: 2 }}>{job.company}</div>
                        <div className="kydoscope-job-type" style={{ fontSize: 14, color: 'rgba(0,0,0,0.5)', fontFamily: 'var(--font-primary)', fontWeight: 400, lineHeight: '23px', marginBottom: 0 }}>{job.jobType}</div>
                        <div className="kydoscope-job-location">{job.location}</div>
                        <div className="kydoscope-job-posted">{job.posted}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  isMobileScreen ? (
                    <div className="kydoscope-no-jobs" style={{flexDirection:'column',justifyContent:'center',alignItems:'center',display:'flex',height:'100%',width:'100%'}}>
                      <div style={{textAlign:'center',marginBottom:18,padding:32}}>
                        <span style={{
                          color: '#1F2937',
                          fontFamily: 'Lora, serif',
                          fontSize: 24,
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '140%',
                          letterSpacing: '0.12px',
                          display: 'block',
                          marginBottom: 2
                        }}>
                          Sorry we couldn’t find any matches for{' '}
                          <span style={{
                            color: '#FF5043',
                            fontFamily: 'Lora, serif',
                            fontSize: 24,
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '140%',
                            letterSpacing: '0.12px',
                          }}>{keyword}</span>
                        </span>
                      </div>
                      <SpotIllustration style={{
                        display: 'flex',
                        width: 276,
                        height: 200,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginBottom: 18
                      }} />
                      <div style={{
                        fontSize: '14px',
                        color: '#575F69',
                        fontFamily: 'Inter, Arial, sans-serif',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '140%',
                        letterSpacing: 0,
                        textAlign: 'center',
                        marginTop: 8,
                        marginBottom: 18
                      }}>
                        No jobs found matching your search.
                      </div>
                      <button
                        className="explore-more-jobs-btn"
                        onClick={() => {
                          setKeyword("");
                          setLocation("");
                          setLocationType([]);
                          setEmploymentType([]);
                          setCompany([]);
                        }}
                        disabled={loadingMore}
                        style={{
                          marginTop: 0,
                          borderColor: '#9CA3AF',
                          color: '#FF5043',
                        }}
                      >
                        {loadingMore ? 'Loading...' : 'Explore More Jobs'}
                      </button>
                    </div>
                  ) : null // Remove Explore More Jobs button from summary section
                )}
                {/* Only show Explore More Jobs if there are jobs */}
                {hasMore && filteredJobs.length > 0 && (
                  <div className="kydoscope-pagination" style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,margin:'24px 0'}}>
                    <button
                      className="explore-more-jobs-btn"
                      onClick={handleNext}
                      disabled={!hasMore}
                    >
                      Explore More Jobs
                    </button>
                    <div style={{fontSize:'.8rem',color:'#575f69',fontWeight:400}}>
                      Showing results 1 – {jobs.length} of {totalCount}
                    </div>
                  </div>
                )}
              </div>
              {!isMobileScreen && (
                <div className="kydoscope-job-detail">
                  {filteredJobs.length > 0 && selectedJob ? (
                    <>
                      <div style={{display: 'flex', justifyContent: 'left', alignItems: 'flex-end', marginBottom: 18}}>
                        <div style={{textAlign: 'left'}}>
                          <div className="kydoscope-job-title" style={{fontWeight: 600, fontSize: '1.23rem', marginBottom: 2}}>{selectedJob.title}</div>
                          <div className="kydoscope-job-location" style={{fontSize: '13.7px',  marginTop: 2}}>{selectedJob.location}</div>
                        </div>
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', marginBottom: 18, marginTop: 8}}>
                        <img
                          src={selectedJob.logo}
                          alt={selectedJob.company}
                          className="kydoscope-job-detail-logo"
                          style={{marginRight: 12}}
                          loading="lazy"
                        />
                        <div className="kydoscope-job-company" style={{fontWeight: 600, fontSize: '1.08rem', marginBottom: 2}}>{selectedJob.company}</div>
                      </div>
                      <div className="kydoscope-job-section-title" style={{marginBottom: 10, marginTop: 18}}>Job Details</div>
                      <div className="kydoscope-job-section-content" style={{marginBottom: 18, fontSize: '13.2px'}}>
                        <dl style={{margin: 0, padding: 0}}>
                          <div style={{display: 'flex', marginBottom: '-2px', alignItems: 'center'}}>
                            <dt style={{ fontWeight: 600, fontSize: '14px',  marginRight: 8, textAlign: 'left' }}>Position:</dt>
                            <dd style={{margin: 0, fontSize: '13.7px', flex: 1,  }}>{selectedJob.jobPosition}</dd>
                          </div>
                          <div style={{display: 'flex', marginBottom: '-2px', alignItems: 'center'}}>
                            <dt style={{ fontWeight: 600, fontSize: '14px',  marginRight: 8, }}>Salary:</dt>
                            <dd style={{margin: 0, fontSize: '13.7px', flex: 1,  }}>{selectedJob.jobSalary}</dd>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center'}}>
                            <dt style={{ fontWeight: 600, fontSize: '14px',  marginRight: 8, textAlign: 'left' }}>Type:</dt>
                            <dd style={{margin: 0, fontSize: '13.7px', flex: 1,  }}>{selectedJob.jobType}</dd>
                          </div>
                        </dl>
                      </div>
                      <div className="kydoscope-job-section-title" style={{marginBottom: 12, marginTop: 18}}>Job Description</div>
                      <div className="kydoscope-job-section-content" style={{fontSize: '13.7px', whiteSpace: 'pre-wrap'}}>{selectedJob.description}</div>
                      <div className="kydoscope-job-section-title" style={{marginBottom: 12, marginTop: 28}}>Job Requirements</div>
                      <div className="kydoscope-job-section-content" style={{marginBottom: 18, fontSize: '13.2px'}}>
                        <dl style={{margin: 0, padding: 0}}>
                          <div style={{display: 'flex', marginBottom: '-2px'}}>
                            <dt style={{ fontWeight: 600, fontSize: '14px', marginRight: 8}}>Education:</dt>
                            <dd style={{margin: 0, fontSize: '13.7px'}}>{selectedJob.requiredEducation}</dd>
                          </div>
                          <div style={{display: 'flex', marginBottom: '-2px'}}>
                            <dt style={{ fontWeight: 600, fontSize: '14px', marginRight: 8}}>Experience:</dt>
                            <dd style={{margin: 0, fontSize: '13.7px'}}>{selectedJob.requiredExperience}</dd>
                          </div>
                          <div style={{display: 'flex'}}>
                            <dt style={{ fontWeight: 600, fontSize: '14px', marginRight: 8}}>Software Skills:</dt>
                            <dd style={{margin: 0, fontSize: '13.7px'}}>{selectedJob.requiredSoftwareSkills}</dd>
                          </div>
                        </dl>
                        <button className="kydoscope-job-apply-btn" style={{ fontWeight: 600, fontSize: '1.05rem'}} onClick={handleApply} disabled={isApplying}>
                          {isApplying ? (<><span className="spinner" style={{marginRight:8}}></span>Applying...</>) : "Apply now"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',width:'100%'}}>
                      <div style={{textAlign:'center',marginBottom:18,paddingBottom:16}}>
                        <span style={{
                          color: '#1F2937',
                          fontFamily: 'Lora, serif',
                          fontSize: 24,
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '140%',
                          letterSpacing: '0.12px',
                          display: 'block',
                          marginBottom: 2
                        }}>
                          Sorry we couldn’t find any matches for{' '}
                          <span style={{
                            color: '#FF5043',
                            fontFamily: 'Lora, serif',
                            fontSize: 24,
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '140%',
                            letterSpacing: '0.12px',
                          }}>{keyword}</span>
                        </span>
                      </div>
                      <SpotIllustration style={{
                        display: 'flex',
                        width: 276,
                        height: 200,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginBottom: 18
                      }} />
                      <div style={{
                        fontSize: '14px',
                        color: '#575F69',
                        fontFamily: 'Inter, Arial, sans-serif',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '140%',
                        letterSpacing: 0,
                        textAlign: 'center',
                        marginTop: 8,
                        marginBottom: 18
                      }}>
                        No jobs found matching your search.
                      </div>
                      <button
                        className="explore-more-jobs-btn"
                        onClick={() => {
                          setKeyword("");
                          setLocation("");
                          setLocationType([]);
                          setEmploymentType([]);
                          setCompany([]);
                        }}
                        disabled={loadingMore}
                        style={{
                          marginTop: 0,
                          borderColor: '#9CA3AF',
                          color: '#FF5043',
                        }}
                      >
                        {loadingMore ? 'Loading...' : 'Explore More Jobs'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </FadeSection>
      </main>
      {isApplying && (
        <Modal isOpen={isApplying} onClose={() => {}} title={null}>
          <div className="modal-content" style={{ textAlign: "center" }}>
            Please wait while we process your application!
            <div style={{ marginTop: 24 }}>
              <Loader />
            </div>
          </div>
        </Modal>
      )}
      {showResumeModal && (
        <ResumeUploadModal
          isOpen={showResumeModal}
          onClose={() => { setShowResumeModal(false); }}
          userId={loggedInUser?.userId}
          onSuccess={() => {
            setShowResumeModal(false);
            setTimeout(() => handleApply(), 0);
          }}
        />
      )}
    </div>
  );
}
